import { useMemo, useState, type ReactNode } from 'react';

/**
 * Picking a driver: the iqama is shown, and the list can be searched.
 *
 * Names in this fleet repeat. There are two men called Muhammad Afzaal and
 * several records reading Naeem Abbas, so a list of names alone asks somebody
 * to choose between rows that look identical. The iqama is the one value unique
 * to a person, so it belongs next to the name at the moment of choosing rather
 * than two screens away.
 *
 * A filter above a native <select>, not a combobox: the select keeps its own
 * keyboard handling, its mobile wheel, and its form semantics, and typing only
 * narrows what it holds. Nothing is reimplemented, so nothing regresses.
 */
export interface DriverOption {
  id: string;
  name: string;
  code?: string | null;
  iqama?: string | null;
  /** Whatever else the call site fetched, so `renderLabel` can still reach it. */
  [k: string]: unknown;
}

/**
 * Normalises the several shapes the API hands back — `fullNameEn` from the REST
 * list, `full_name_en` from the raw-SQL pickers, plain `name` from others — so
 * a call site can pass whatever it already fetched.
 *
 * The original record is spread through, because some screens show more than
 * the name (whether a driver is free, which truck they are already on) and
 * normalising must not cost them that.
 */
export function toDriverOption(d: any): DriverOption {
  return {
    ...d,
    id: String(d?.id ?? ''),
    name: d?.name ?? d?.fullNameEn ?? d?.full_name_en ?? d?.code ?? '—',
    code: d?.code ?? null,
    iqama: d?.iqama ?? d?.iqamaNumber ?? d?.iqama_number ?? null,
  };
}

/** How one driver reads in the list. */
export function driverLabel(d: DriverOption): string {
  const bits = [d.name];
  if (d.iqama) bits.push(d.iqama);
  else if (d.code) bits.push(d.code);
  return bits.join(' · ');
}

export function DriverSelect({
  value,
  onChange,
  drivers,
  id,
  className = '',
  disabled = false,
  noneLabel = '— no driver —',
  required = false,
  searchThreshold = 8,
  renderLabel,
  extraOptions,
  autoFocus = false,
  'aria-label': ariaLabel,
}: {
  value: string;
  onChange: (id: string) => void;
  drivers: DriverOption[];
  id?: string;
  className?: string;
  disabled?: boolean;
  noneLabel?: string;
  required?: boolean;
  /** Below this many drivers the search box is noise, so it is not shown. */
  searchThreshold?: number;
  /**
   * For screens that show more than the name — whether a driver is free, which
   * truck they are already on. Normalising the list must not cost them that.
   */
  renderLabel?: (d: DriverOption) => string;
  /** Options that are not drivers, e.g. an explicit "unassign". */
  extraOptions?: ReactNode;
  /** Focus on mount — the search box when there is one, else the list. */
  autoFocus?: boolean;
  'aria-label'?: string;
}) {
  const [q, setQ] = useState('');

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return drivers;
    return drivers.filter((d) => {
      // The chosen driver always stays in the list. Without this, typing a name
      // that does not match the current selection would empty the select, read
      // as "no driver", and saving would clear somebody nobody meant to touch.
      if (d.id === value) return true;
      return `${d.name} ${d.code ?? ''} ${d.iqama ?? ''}`.toLowerCase().includes(needle);
    });
  }, [drivers, q, value]);

  const showSearch = drivers.length >= searchThreshold;
  // Distinguishes "nothing matches what you typed" from "there are no drivers",
  // which are different problems with different fixes.
  const noMatches = showSearch && q.trim() !== '' && shown.every((d) => d.id === value);

  return (
    <div className="space-y-1">
      {showSearch && (
        <input
          type="search"
          // Typing is the first thing somebody does with a long list, so the
          // search takes the focus and the select takes it only when there is
          // no search to take it.
          autoFocus={autoFocus}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          disabled={disabled}
          placeholder="Search name, code or iqama…"
          aria-label="Search drivers"
          className="h-8 w-full rounded-md border border-input bg-background px-2 text-xs"
        />
      )}
      <select
        id={id}
        autoFocus={autoFocus && !showSearch}
        aria-label={ariaLabel}
        required={required}
        disabled={disabled}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={className || 'h-10 w-full rounded-md border border-input bg-background px-2 text-sm'}
      >
        <option value="">{noneLabel}</option>
        {extraOptions}
        {shown.map((d) => (
          <option key={d.id} value={d.id}>{(renderLabel ?? driverLabel)(d)}</option>
        ))}
      </select>
      {noMatches && (
        <p className="text-[11px] text-muted-foreground">No driver matches “{q.trim()}”.</p>
      )}
    </div>
  );
}
